import React, { useState, useEffect, useRef } from "react";
import { Col, Row } from "reactstrap";
import {
  message,
  Input,
  Button,
  Form,
  Space,
  Tooltip,
  Drawer,
  Card,
  Select,
  Modal,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import moment from "moment";
import {
  insertMessage,
  updateMessage,
  deleteMessage,
  getAllMessage,
} from "../../helpers/helper";
import DataTable from "../../Components/Common/DataTable";
import Editor from "../../common/Editor";
import { useTranslation } from "react-i18next";

const { Option } = Select;
const { confirm } = Modal;
const Roles = () => {
  const { t } = useTranslation();
  document.title = t("messageManager");

  const [form] = Form.useForm();
  const [formSearch] = Form.useForm();
  const [listBot, setListBot] = useState([]);
  const [visibleForm, setVisibleForm] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");
  const nameRefId = useRef("common1");
  const [content, setContent] = useState(null);

  //pagination
  const [totalPage, setTotalPage] = useState(1);
  const [pageIndex, setPageIndex] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const data = await getAllData();
      setListBot(data);
    } catch (error) {
      message.error(t("requiredPermission"));
    }
  }

  const getAllData = async (_prams) => {
    setLoading(true);
    try {
      const params = _prams ? _prams : { pageIndex: 1, pageSize: 10 };
      const dataRes = await getAllMessage(params);
      setTotalPage(dataRes?.data.totalPages);
      let data = convertDataTable(dataRes.data.data);
      setLoading(false);
      return data.length ? data : false;
    } catch (error) {
      setLoading(false);
      setTotalPage(0);
      message.error(t("requiredPermission"));
    }
  };

  const convertDataTable = (dataArray) => {
    let data =
      dataArray.length > 0 &&
      dataArray.map((item) => {
        return {
          key: item._id,
          name: item.name,
          content: item.content.replace(/\n/g, "<br>"),
          botToken: item.botToken,
          createdTime: moment(new Date(item.createdTime)).format(
            "hh:mm DD/MM/YYYY"
          ),
        };
      });
    return dataArray ? data : [];
  };

  const columns = [
    {
      title: t("titleMess"),
      dataIndex: "name",
    },
    {
      title: t("content"),
      dataIndex: "content",
      render: (_, record) => (
        <div
          style={{ maxWidth: "400px" }}
          dangerouslySetInnerHTML={{ __html: record.content }}
        ></div>
      ),
    },

    {
      title: t("createdTime"),
      dataIndex: "createdTime",
    },
    {
      title: t("action"),
      dataIndex: "",
      render: (_, record) => (
        <Space>
          <Tooltip title={t("edit")}>
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record.key)}
            />
          </Tooltip>
          <Tooltip title={t("delete")}>
            <Button
              type="danger"
              shape="circle"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => showModalConfirmDelete(record.key)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const onFinish = async (data) => {
    let contentFormat = content
      .replace(/<p>/g, "")
      .replace(/&nbsp;/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/"/g, "'")
      .replace(/<br>/g, " \n ");

    const dataReq = {
      name: data.name.trim(),
      content: contentFormat.trim(),
    };

    try {
      if (!data.id) {
        const dataRes = await insertMessage(dataReq);
        if (dataRes.status === "success") {
          message.success(t("saveSuccess"));
        } else {
          message.error(t("saveFail"));
        }
        formSearch.resetFields();
        fetchData();
      } else {
        const dataRes = await updateMessage(data.id, dataReq);
        if (dataRes.status === "success") {
          message.success(t("updateSuccess"));
        } else {
          message.error(t("updateFail"));
        }
      }
      form.resetFields();
      fetchData();
      onClose();
    } catch (error) {
      if (error.response.status === 400) {
        return message.error(t("errorMess"));
      }
      message.error(t("permisstionDeniedAction"));
      formSearch.resetFields();
      form.resetFields();
      onClose();
    }
  };

  const onClose = () => {
    setContent("");
    form.resetFields();
    setVisibleForm(false);
  };

  const showDrawer = () => {
    setVisibleForm(true);
  };

  const handleNewBot = () => {
    setContent("");
    setDrawerTitle(t("createMess"));

    form.resetFields();
    showDrawer();
  };

  const showModalConfirmDelete = (record) => {
    confirm({
      title: t("confirmDelete"),
      icon: <ExclamationCircleFilled />,
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk() {
        onDelete(record);
      },
      onCancel() {},
    });
  };

  const onDelete = async (key) => {
    try {
      const dataRes = await deleteMessage(key);
      if (dataRes.status === "success") {
        message.success(t("deleteSuccess"));
        fetchData();
      } else {
        message.error(t("deleteFail"));
      }
    } catch (error) {
      message.error(t("permisstionDeniedAction"));
    }
  };

  const onEdit = (key) => {
    const dataEdit = listBot.filter((item) => item.key === key);
    form.setFieldsValue({
      name: dataEdit[0].name,
      id: dataEdit[0].key,
    });

    dataEdit[0].content = dataEdit[0].content.replace(/\n/g, "<br>");
    setContent(`<p>${dataEdit[0].content}</p>` || "");
    setDrawerTitle(t("edit"));
    showDrawer();
  };

  const onPageChange = (page, page_size) => {
    setPageIndex(page);
    setPageSize(page_size);
    onPageChangeAtSearch();
  };

  const onPageChangeAtSearch = async () => {
    const dataForm = formSearch.getFieldsValue();
    const params = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      name: dataForm.name || "",
    };
    const dataRes = await getAllData(params);
    setListBot(dataRes.length > 0 ? dataRes : false);
  };

  const handleSearch = async () => {
    const dataForm = formSearch.getFieldsValue();

    const params = {
      pageIndex: 1,
      pageSize: pageSize,
      name: dataForm.name || "",
    };
    const dataRes = await getAllData(params);
    setListBot(dataRes.length > 0 ? dataRes : false);
    setPageIndex(1);
  };

  const handleRefresh = async () => {
    formSearch.resetFields();
    setPageIndex(1);
    setPageSize(10);

    const _params = {
      pageIndex: pageIndex,
      pageSize: pageSize,
    };
    const dataRes = await getAllData(_params);
    setListBot(dataRes);
  };

  return (
    <>
      <div className="page-content">
        <Card bordered={false}>
          <Row>
            <Col xs={12}>
              <Form form={formSearch} layout="vertical" autoComplete="off">
                <Row>
                  <Col sm={3}>
                    <Form.Item name="name" label={t("titleMess") + ":"}>
                      <Input
                        placeholder={t("placeHolderTitleMess")}
                        allowClear={true}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={3}>
                    <Form.Item className="mt-4">
                      <Space>
                        <Button
                          type="primary"
                          htmlType="button"
                          disabled={loading}
                          onClick={() => handleSearch()}
                        >
                          {t("search")}
                        </Button>

                        <Button type="primary" onClick={handleNewBot}>
                          {t("createMess")}
                        </Button>

                        <Button
                          type="primary"
                          htmlType="button"
                          disabled={loading}
                          onClick={() => handleRefresh()}
                        >
                          {t("reload")}
                        </Button>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Card>
        <div>
          <Col>
            <Drawer
              title={drawerTitle}
              placement={"right"}
              width="50%"
              onClose={onClose}
              open={visibleForm}
              bodyStyle={{
                paddingBottom: 80,
              }}
              style={{ marginTop: "70px" }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
              >
                <Row>
                  <Col hidden={true}>
                    <Form.Item name="id" label="Id">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Form.Item
                    name="name"
                    label={t("titleMess")}
                    rules={[
                      {
                        required: true,
                        message: t("validateTitleMess"),
                      },
                      {
                        type: "string",
                        min: 1,
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("placeHolderTitleMess")}
                      allowClear={true}
                    />
                  </Form.Item>
                  <Form.Item label={t("messenger")}>
                    <Editor
                      onChange={(v) => setContent(v)}
                      valueName={content}
                      refId={nameRefId}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Typography>
                      <ul title={t("note.title")}>
                        <b>{t("note.title")}</b>
                        <li>{t("note.1")}</li>
                        <li>{t("note.2")}</li>
                        <li>{t("note.3")}</li>
                        <li>{t("note.4")}</li>
                        <li>{t("note.5")}</li>
                      </ul>
                    </Typography>
                  </Form.Item>
                </Row>

                <Form.Item className="mt-3">
                  <Space>
                    <Button type="primary" htmlType="submit">
                      {t("save")}
                    </Button>

                    <Button type="danger" onClick={onClose}>
                      {t("close")}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Drawer>
          </Col>
        </div>
        <DataTable
          listData={listBot}
          pageSize={pageSize}
          columns={columns}
          indexPage={pageIndex}
          totalPage={totalPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </div>
    </>
  );
};
export default Roles;
